import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * ProfilePicture Component
 * Displays user profile picture or initials fallback
 * @param {string} profilePictureUrl - URL of the profile picture
 * @param {string} initials - User initials (e.g., "JD")
 * @param {string} size - Size of the avatar ('sm', 'md', 'lg', 'xl')
 * @param {boolean} showEditButton - Show edit button overlay
 * @param {function} onEditClick - Callback when edit button is clicked
 * @param {string} className - Additional CSS classes
 */
const ProfilePicture = ({
  profilePictureUrl,
  initials,
  size = 'md',
  showEditButton = false,
  onEditClick,
  className = '',
}) => {
  const { t } = useTranslation('profile');
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-base',
    xl: 'w-24 h-24 text-xl',
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  // Generate background color based on initials
  const getBackgroundColor = (initials) => {
    if (!initials) return 'bg-slate-400';
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500',
    ];
    const index = initials.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {profilePictureUrl ? (
        <div
          className={`${sizeClass} rounded-full bg-cover bg-center bg-no-repeat relative ring-2 ring-background`}
          style={{ backgroundImage: `url("${profilePictureUrl}")` }}
        >
          {showEditButton && (
            <button
              onClick={onEditClick}
              className="absolute inset-0 rounded-full bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
              aria-label={t('editProfilePicture')}
            >
              <span className="material-symbols-outlined text-white text-lg">edit</span>
            </button>
          )}
        </div>
      ) : (
        <div
          className={`${sizeClass} rounded-full ${getBackgroundColor(initials)} text-white flex items-center justify-center font-semibold relative ring-2 ring-background`}
        >
          {initials || '?'}
          {showEditButton && (
            <button
              onClick={onEditClick}
              className="absolute inset-0 rounded-full bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
              aria-label={t('editProfilePicture')}
            >
              <span className="material-symbols-outlined text-white text-lg">edit</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilePicture;

